import { create } from 'zustand'
import { jobs as jobsApi, type Job, type CreateJobParams } from '../lib/api'

interface JobsState {
  jobs: Job[]
  currentJob: Job | null
  isLoading: boolean
  error: string | null
  fetchJobs: () => Promise<void>
  fetchJob: (id: string) => Promise<void>
  createJob: (params: CreateJobParams) => Promise<Job>
  updateJob: (job: Job) => void
  clearCurrentJob: () => void
}

export const useJobsStore = create<JobsState>((set, get) => ({
  jobs: [],
  currentJob: null,
  isLoading: false,
  error: null,

  fetchJobs: async () => {
    set({ isLoading: true, error: null })
    try {
      const jobs = await jobsApi.listJobs()
      set({ jobs, isLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load jobs'
      set({ error: message, isLoading: false })
    }
  },

  fetchJob: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const job = await jobsApi.getJob(id)
      set({ currentJob: job, isLoading: false })
      // Also update in the list
      const jobs = get().jobs.map((j) => (j.id === id ? job : j))
      set({ jobs })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load job'
      set({ error: message, isLoading: false })
    }
  },

  createJob: async (params: CreateJobParams) => {
    const job = await jobsApi.createJob(params)
    set((state) => ({ jobs: [job, ...state.jobs] }))
    return job
  },

  updateJob: (job: Job) => {
    set((state) => ({
      currentJob: state.currentJob?.id === job.id ? job : state.currentJob,
      jobs: state.jobs.map((j) => (j.id === job.id ? job : j)),
    }))
  },

  clearCurrentJob: () => set({ currentJob: null }),
}))
